import React, { useEffect } from 'react';
// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in Argentine Pesos (ARS).
// ============================================================
export type ProductKey = 'plus' | 'basic';
export const PRODUCTS: Record<ProductKey, {
  id: string;
  name: string;
  value: number;
  currency: 'ARS';
  checkoutUrl: string;
}> = {
  plus: {
    id: 'comercio-plus',
    name: 'MEGAPACK + POTENCIADORES',
    value: 34990,
    currency: 'ARS',
    checkoutUrl: 'http://centershop.store/cart/47923842646167:1'
  },
  basic: {
    id: 'comercio-basic',
    name: 'MEGAPACK COMERCIO PRODUCTIVO 2026',
    value: 22990,
    currency: 'ARS',
    checkoutUrl: 'http://centershop.store/cart/47923837960343:1'
  }
};
const PIXEL_ID = '4298781313716057';
// Safely access fbq if available
const getFbq = (): ((...args: any[]) => void) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return typeof w.fbq === 'function' ? w.fbq : null;
};
// ============================================================
// Tracking helpers — use these from anywhere in the app
// ============================================================
export const trackInitiateCheckout = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  fbq('track', 'InitiateCheckout', {
    value: p.value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: p.value
    }],
    num_items: 1
  });
};
export const trackPurchase = (productKey: ProductKey, overrideValue?: number) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  const value = typeof overrideValue === 'number' ? overrideValue : p.value;
  fbq('track', 'Purchase', {
    value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: value
    }],
    num_items: 1
  });
};
export const trackViewContent = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  fbq('track', 'ViewContent', {
    value: p.value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product'
  });
};
// ============================================================
// MetaPixel component — initializes the pixel and handles
// the Purchase return URL (?purchase=plus|basic[&value=...])
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    // Build the Meta queue immediately, so checkout clicks are never lost
    // while the remote script finishes loading.
    if (!w.fbq) {
      const n: any = w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
    }

    // The guard prevents duplicate PageView events in React development mode,
    // while still initializing this pixel if another fbq instance already exists.
    if (!w.__centerShopMetaPixelInitialized) {
      w.__centerShopMetaPixelInitialized = true;
      w.fbq('init', PIXEL_ID);
      w.fbq('track', 'PageView');
      const loadScript = () => {
        if (document.getElementById('fb-pixel-script')) return;
        const script = document.createElement('script');
        script.id = 'fb-pixel-script';
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
      };
      if ('requestIdleCallback' in w) {
        w.requestIdleCallback(loadScript, {
          timeout: 2500
        });
      } else {
        setTimeout(loadScript, 1200);
      }
    }
    // Handle Purchase return URL:
    // e.g. https://landing.com/?purchase=plus   → fires Purchase for the plus offer
    // e.g. https://landing.com/?purchase=basic  → fires Purchase for the basic offer
    // Optional: &value=27990 overrides the value (useful if the checkout sends the actual paid amount)
    try {
      const params = new URLSearchParams(window.location.search);
      const purchaseProduct = params.get('purchase') as ProductKey | null;
      if (purchaseProduct === 'plus' || purchaseProduct === 'basic') {
        const valueParam = params.get('value');
        const overrideValue = valueParam ? Number(valueParam) : undefined;
        // Defer until pixel script has had a chance to load
        const fire = () => {
          const fbq = getFbq();
          if (fbq) {
            trackPurchase(purchaseProduct, overrideValue);
            // Clean the query string so a refresh doesn't double-fire
            const url = new URL(window.location.href);
            url.searchParams.delete('purchase');
            url.searchParams.delete('value');
            window.history.replaceState({}, '', url.toString());
          } else {
            // fbq not ready yet — retry shortly
            setTimeout(fire, 300);
          }
        };
        fire();
      }
    } catch (e) {
      // no-op
    }
  }, []);
  return null;
}